import { useMemo, useState } from 'react';
import { Button, Card, DataTable, InlineStack, Pagination, Select, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { Delta } from './Delta';
import { MONTH_LABELS, formatInteger, formatPercent, getYears, toNumber } from './analyticsHelpers';

const QUARTER_LABELS = ['Q1 (Jan–Mar)', 'Q2 (Apr–Jun)', 'Q3 (Jul–Sep)', 'Q4 (Oct–Dec)'];

function formatValue(value, format) {
  if (format === 'integer') return formatInteger(value);
  return formatToCurrency(toNumber(value));
}

// Generic monthly/quarterly/yearly breakdown table with a year flipper.
// rows: [{ year, month, ...metrics }] for all years at once, so flipping is instant.
// Pass onPeriodClick({ year, month?, quarter? }) to make the period labels drillable.
export default function PeriodBreakdownTable({ title, subtitle, rows, columns, netKey, netLabel, invertDelta = false, loading = false, onPeriodClick }) {
  const [mode, setMode] = useState('MONTH');
  const years = useMemo(() => getYears(rows), [rows]);
  const [selectedYear, setSelectedYear] = useState(null);
  const year = selectedYear && years.includes(selectedYear) ? selectedYear : years[years.length - 1];

  const metricKeys = useMemo(() => {
    const keys = columns.map((column) => column.key);
    if (netKey && !keys.includes(netKey)) keys.push(netKey);
    return keys;
  }, [columns, netKey]);

  const aggregates = useMemo(() => {
    // Zero-filled lookup per year-month plus totals per quarter and year
    const byMonth = {};
    const byQuarter = {};
    const byYear = {};
    const emptyEntry = () => metricKeys.reduce((entry, key) => ({ ...entry, [key]: 0 }), {});

    (rows || []).forEach((row) => {
      const y = Number(row.year);
      const m = Number(row.month);
      if (!Number.isFinite(y) || !Number.isFinite(m)) return;
      const q = Math.floor((m - 1) / 3) + 1;
      byMonth[`${y}-${m}`] = byMonth[`${y}-${m}`] || emptyEntry();
      byQuarter[`${y}-${q}`] = byQuarter[`${y}-${q}`] || emptyEntry();
      byYear[y] = byYear[y] || emptyEntry();
      metricKeys.forEach((key) => {
        const value = toNumber(row[key]);
        byMonth[`${y}-${m}`][key] += value;
        byQuarter[`${y}-${q}`][key] += value;
        byYear[y][key] += value;
      });
    });

    return { byMonth, byQuarter, byYear, emptyEntry };
  }, [rows, metricKeys]);

  const displayRows = useMemo(() => {
    const { byMonth, byQuarter, byYear, emptyEntry } = aggregates;
    const now = new Date();
    if (mode === 'YEAR') {
      return [...years].reverse().map((y) => ({
        label: y === now.getFullYear() ? `${y} (YTD)` : String(y),
        period: { year: y },
        values: byYear[y] || emptyEntry(),
        previous: byYear[y - 1] || null
      }));
    }
    if (!year) return [];
    if (mode === 'QUARTER') {
      return QUARTER_LABELS.map((label, idx) => ({
        label,
        period: { year, quarter: idx + 1 },
        values: byQuarter[`${year}-${idx + 1}`] || emptyEntry(),
        previous: byQuarter[`${year - 1}-${idx + 1}`] || null,
        isFuture: new Date(year, idx * 3, 1) > now
      }));
    }
    return MONTH_LABELS.map((label, idx) => ({
      label,
      period: { year, month: idx + 1 },
      values: byMonth[`${year}-${idx + 1}`] || emptyEntry(),
      previous: byMonth[`${year - 1}-${idx + 1}`] || null,
      isFuture: new Date(year, idx, 1) > now
    }));
  }, [aggregates, mode, year, years]);

  const yearTotals = year ? aggregates.byYear[year] : null;
  const prevYearTotals = year ? aggregates.byYear[year - 1] : null;
  const yearNetTotal = yearTotals ? toNumber(yearTotals[netKey]) : 0;

  // For the running year compare YTD against the same months of the previous year,
  // otherwise a partial year vs a full year always reads as a big decline.
  const now = new Date();
  let prevComparableNet = prevYearTotals ? toNumber(prevYearTotals[netKey]) : null;
  if (year === now.getFullYear()) {
    let sum = 0;
    let hasData = false;
    for (let m = 1; m <= now.getMonth() + 1; m++) {
      const entry = aggregates.byMonth[`${year - 1}-${m}`];
      if (entry) {
        sum += toNumber(entry[netKey]);
        hasData = true;
      }
    }
    prevComparableNet = hasData ? sum : null;
  }

  const showPeriodExtras = mode !== 'YEAR';
  const headings = [
    <b>{mode === 'YEAR' ? 'Year' : mode === 'QUARTER' ? 'Quarter' : 'Month'}</b>,
    ...columns.map((column) => <b>{column.label}</b>),
    <b>Δ vs prev. year</b>,
    ...(showPeriodExtras ? [<b>Cumulative {netLabel}</b>, <b>% of year</b>] : [])
  ];
  const columnContentTypes = ['text', ...columns.map(() => 'numeric'), 'numeric', ...(showPeriodExtras ? ['numeric', 'numeric'] : [])];

  let runningTotal = 0;
  const tableRows = displayRows.map((row) => {
    const net = toNumber(row.values[netKey]);
    runningTotal += net;
    const label = onPeriodClick ? (
      <Button variant="plain" onClick={() => onPeriodClick(row.period)}>
        {row.label}
      </Button>
    ) : (
      row.label
    );
    return [
      label,
      ...columns.map((column) => formatValue(row.values[column.key], column.format)),
      row.isFuture ? '—' : <Delta current={net} previous={row.previous ? row.previous[netKey] : null} invert={invertDelta} />,
      ...(showPeriodExtras ? [formatToCurrency(runningTotal), yearNetTotal ? formatPercent(net / yearNetTotal) : '—'] : [])
    ];
  });

  const totals = showPeriodExtras
    ? [
        '',
        ...columns.map((column) => formatValue(yearTotals ? yearTotals[column.key] : 0, column.format)),
        <Delta current={yearNetTotal} previous={prevComparableNet} invert={invertDelta} />,
        formatToCurrency(yearNetTotal),
        yearNetTotal ? formatPercent(1) : '—'
      ]
    : null;

  const yearIndex = years.indexOf(year);

  return (
    <Card>
      <CardHeading title={title} />
      {subtitle ? (
        <Text tone="subdued" as="p">
          {subtitle}
        </Text>
      ) : null}
      <div style={{ margin: '12px 0' }}>
        <InlineStack align="space-between" blockAlign="center">
          <div style={{ width: '160px' }}>
            <Select
              label="View"
              labelHidden
              options={[
                { label: 'Per month', value: 'MONTH' },
                { label: 'Per quarter', value: 'QUARTER' },
                { label: 'Per year', value: 'YEAR' }
              ]}
              value={mode}
              onChange={setMode}
            />
          </div>
          {mode !== 'YEAR' && year ? (
            <Pagination
              hasPrevious={yearIndex > 0}
              hasNext={yearIndex < years.length - 1}
              onPrevious={() => setSelectedYear(years[yearIndex - 1])}
              onNext={() => setSelectedYear(years[yearIndex + 1])}
              label={<b>{year}</b>}
            />
          ) : null}
        </InlineStack>
      </div>
      {loading ? (
        <SkeletonBodyText lines={8} />
      ) : (
        <DataTable verticalAlign="middle" increasedTableDensity stickyHeader columnContentTypes={columnContentTypes} headings={headings} rows={tableRows} totals={totals} showTotalsInFooter />
      )}
    </Card>
  );
}
